import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';
import logger from '@/logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

export type RiskNotificationPhone = {
	id: string;
	organization_id: string;
	phone_number: string;
	name: string | null;
	is_active: boolean;
	created_at: string;
	updated_at: string;
};

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

export function isValidE164(phone: string): boolean {
	const normalized = phone.startsWith('+') ? phone : `+${phone}`;
	return E164_REGEX.test(normalized);
}

export function normalizeToE164(phone: string): string {
	const digits = phone.replace(/\D/g, '');
	if (digits.length === 10 && digits.startsWith('52')) {
		return `+${digits}`;
	}
	if (digits.length === 10) {
		return `+52${digits}`;
	}
	return phone.startsWith('+') ? phone : `+${phone}`;
}

interface RiskNotificationPhonesStore {
	phones: RiskNotificationPhone[];
	isLoading: boolean;
	error: string | null;

	loadPhones: (organizationId: string) => Promise<void>;
	insertPhone: (
		organizationId: string,
		phoneNumber: string,
		name?: string | null
	) => Promise<boolean>;
	updatePhone: (
		id: string,
		organizationId: string,
		updates: { name?: string | null; phone_number?: string; is_active?: boolean }
	) => Promise<boolean>;
	toggleActive: (id: string, organizationId: string, isActive: boolean) => Promise<boolean>;
	deletePhone: (id: string, organizationId: string) => Promise<boolean>;
	resetError: () => void;
}

export const useRiskNotificationPhonesStore = create<RiskNotificationPhonesStore>((set, get) => ({
	phones: [],
	isLoading: false,
	error: null,

	loadPhones: async (organizationId: string) => {
		try {
			set({ isLoading: true, error: null });

			const { data, error } = await supabase
				.from('organization_risk_notification_phones')
				.select('*')
				.eq('organization_id', organizationId)
				.order('created_at', { ascending: true });

			if (error) {
				logger.error('Error loading risk notification phones:', error);
				set({ error: error.message, isLoading: false });
				return;
			}

			set({
				phones: data || [],
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading risk notification phones:', err);
			set({
				error: 'Failed to load risk notification phones',
				isLoading: false
			});
		}
	},

	insertPhone: async (organizationId: string, phoneNumber: string, name?: string | null) => {
		try {
			set({ error: null });

			const normalized = normalizeToE164(phoneNumber);
			if (!isValidE164(normalized)) {
				set({ error: 'Invalid phone format. Use E.164 (e.g. +525512345678)' });
				return false;
			}

			const { error: insertError } = await supabase
				.from('organization_risk_notification_phones')
				.insert({
					organization_id: organizationId,
					phone_number: normalized,
					name: name?.trim() || null,
					is_active: true
				});

			if (insertError) {
				logger.error('Error inserting risk notification phone:', insertError);
				set({ error: insertError.message });
				return false;
			}

			await get().loadPhones(organizationId);
			return true;
		} catch (err) {
			logger.error('Error inserting risk notification phone:', err);
			set({ error: 'Failed to insert risk notification phone' });
			return false;
		}
	},

	updatePhone: async (
		id: string,
		organizationId: string,
		updates: { name?: string | null; phone_number?: string; is_active?: boolean }
	) => {
		try {
			set({ error: null });

			if (updates.phone_number !== undefined) {
				const normalized = normalizeToE164(updates.phone_number);
				if (!isValidE164(normalized)) {
					set({ error: 'Invalid phone format. Use E.164 (e.g. +525512345678)' });
					return false;
				}
				updates = { ...updates, phone_number: normalized };
			}

			const payload: Record<string, unknown> = {
				updated_at: new Date().toISOString()
			};
			if (updates.name !== undefined) payload.name = updates.name?.trim() || null;
			if (updates.phone_number !== undefined) payload.phone_number = updates.phone_number;
			if (updates.is_active !== undefined) payload.is_active = updates.is_active;

			const { error: updateError } = await supabase
				.from('organization_risk_notification_phones')
				.update(payload)
				.eq('id', id);

			if (updateError) {
				logger.error('Error updating risk notification phone:', updateError);
				set({ error: updateError.message });
				return false;
			}

			await get().loadPhones(organizationId);
			return true;
		} catch (err) {
			logger.error('Error updating risk notification phone:', err);
			set({ error: 'Failed to update phone' });
			return false;
		}
	},

	toggleActive: async (id: string, organizationId: string, isActive: boolean) => {
		try {
			set({ error: null });

			const { error: updateError } = await supabase
				.from('organization_risk_notification_phones')
				.update({ is_active: isActive, updated_at: new Date().toISOString() })
				.eq('id', id);

			if (updateError) {
				logger.error('Error toggling risk notification phone:', updateError);
				set({ error: updateError.message });
				return false;
			}

			await get().loadPhones(organizationId);
			return true;
		} catch (err) {
			logger.error('Error toggling risk notification phone:', err);
			set({ error: 'Failed to update risk notification phone' });
			return false;
		}
	},

	deletePhone: async (id: string, organizationId: string) => {
		try {
			set({ error: null });

			const { error: deleteError } = await supabase
				.from('organization_risk_notification_phones')
				.delete()
				.eq('id', id);

			if (deleteError) {
				logger.error('Error deleting risk notification phone:', deleteError);
				set({ error: deleteError.message });
				return false;
			}

			await get().loadPhones(organizationId);
			return true;
		} catch (err) {
			logger.error('Error deleting risk notification phone:', err);
			set({ error: 'Failed to delete risk notification phone' });
			return false;
		}
	},

	resetError: () => set({ error: null })
}));
